import type { FC, ReactNode } from "react";

import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
  Field,
  Label,
} from "@headlessui/react";

import { clsx, Icon } from "@alpacahq/ui/old";

import type { CryptoSelectOption } from "../CryptoSelect";

export interface CryptoComboboxPresProps {
  label: string;
  value: string;
  placeholder: string;
  className?: string;
  disabled: boolean;
  emptyMessage?: ReactNode;
  errorMessage?: string;
  required?: boolean;

  selectedOption: CryptoSelectOption | undefined;
  filteredOptions: CryptoSelectOption[];
  showPanel: boolean;
  showSelectedIcon: boolean;
  displayValue: (v: string) => string;

  onSelect: (v: string | null) => void;
  onQueryChange: (q: string) => void;
  onBlur: () => void;
}

export const CryptoComboboxPres: FC<CryptoComboboxPresProps> = ({
  label,
  value,
  placeholder,
  className,
  disabled,
  emptyMessage,
  errorMessage,
  required,
  selectedOption,
  filteredOptions,
  showPanel,
  showSelectedIcon,
  displayValue,
  onSelect,
  onQueryChange,
  onBlur,
}) => (
  <Field as="div" className={clsx("relative", className)}>
    <Label className="mb-1 block font-normal text-gray-800 text-sm">
      {label}
      {required && <span className="text-red-500">*</span>}
    </Label>
    <Combobox value={value} onChange={onSelect} disabled={disabled}>
      <div className="relative">
        {showSelectedIcon && (
          <div className="pointer-events-none absolute inset-y-0 left-0 z-10 flex items-center pl-3">
            <div className="flex h-5 w-5 items-center justify-center">
              {selectedOption?.icon}
            </div>
          </div>
        )}
        <ComboboxInput
          className={clsx(
            "relative w-full rounded border bg-white py-2 pr-10 text-left font-medium shadow-sm placeholder:font-medium placeholder:text-gray-500 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 disabled:cursor-not-allowed disabled:opacity-50 sm:text-sm",
            showSelectedIcon ? "pl-10" : "pl-3",
            errorMessage
              ? "border-rose-500 text-rose-500"
              : "border-gray-400 text-gray-950",
          )}
          displayValue={displayValue}
          onChange={(e) => onQueryChange(e.target.value)}
          onBlur={onBlur}
          placeholder={placeholder}
        />
        <ComboboxButton className="absolute inset-y-0 right-0 flex items-center pr-2">
          <Icon name="ChevronDown" className="h-5 w-5 text-gray-800" />
        </ComboboxButton>
      </div>
      {showPanel && (
        <ComboboxOptions className="absolute z-20 mt-1 flex max-h-60 w-full flex-col overflow-hidden rounded border border-gray-400 bg-white text-base focus:outline-none sm:text-sm">
          <div className="min-h-0 flex-1 overflow-auto">
            {filteredOptions.length > 0 ? (
              <div className="py-1">
                {filteredOptions.map((option) => {
                  const isSelected = option.value === value;
                  return (
                    <ComboboxOption
                      key={option.value}
                      value={option.value}
                      className={({ selected }) =>
                        clsx(
                          "relative cursor-pointer select-none px-3.5 py-1.5",
                          isSelected
                            ? "bg-gray-100 text-gray-900"
                            : selected
                              ? "bg-indigo-100 text-indigo-900"
                              : "text-gray-900",
                        )
                      }
                    >
                      <div className="flex items-center gap-1.5">
                        {option.icon && (
                          <div className="flex h-5 w-5 items-center justify-center">
                            {option.icon}
                          </div>
                        )}
                        <div
                          className={clsx(
                            "flex-1 truncate",
                            isSelected ? "font-medium" : "font-normal",
                          )}
                        >
                          {option.label}
                        </div>
                      </div>
                    </ComboboxOption>
                  );
                })}
              </div>
            ) : (
              emptyMessage
            )}
          </div>
        </ComboboxOptions>
      )}
    </Combobox>
    {errorMessage && (
      <p className="m-0 mt-1 p-0 text-red-500 text-xs">{errorMessage}</p>
    )}
  </Field>
);
